import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, MapPin, CheckCircle } from 'lucide-react';

const serviceAreas = [
  {
    name: 'St. Augustine',
    description: 'Our home base. The oldest city in America deserves the finest craftsmanship.',
    primary: true,
  },
  {
    name: 'Jacksonville',
    description: 'Serving homeowners throughout the Jacksonville metro area.',
    primary: true,
  },
  {
    name: 'Ponte Vedra',
    description: 'Luxury remodeling for Ponte Vedra\'s discerning homeowners.',
    primary: true,
  },
  {
    name: 'Nocatee',
    description: 'Modern updates for Nocatee\'s growing community.',
    primary: false,
  },
  {
    name: 'St. Johns County',
    description: 'Comprehensive coverage throughout St. Johns County.',
    primary: false,
  },
  {
    name: 'Palm Coast',
    description: 'Quality remodeling services for Palm Coast residents.',
    primary: false,
  },
];

const neighborhoods = [
  'St. Augustine Beach',
  'Vilano Beach',
  'Anastasia Island',
  'World Golf Village',
  'Julington Creek',
  'Fruit Cove',
  'Mandarin',
  'San Marco',
  'Riverside',
  'Avondale',
  'Neptune Beach',
  'Atlantic Beach',
  'Jacksonville Beach',
  'Sawgrass',
  'TPC Sawgrass',
  'Marsh Landing',
  'Palm Valley',
  'Durbin Crossing',
  'Bartram Park',
  'St. Johns',
];

export const ServiceAreas = () => {
  return (
    <main data-testid="service-areas-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="w-16 h-px bg-[#C6A87C] mb-8" />
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6">Service Areas</h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              Proudly serving homeowners throughout Northeast Florida. From St. Augustine to Jacksonville and everywhere in between, we bring luxury remodeling to your door.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="aspect-square md:aspect-[4/3] overflow-hidden"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d220366.9694127147!2d-81.59567399999999!3d29.9012735!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e428b89d10b24f%3A0x1e55ceda80e8a01f!2sSt.%20Augustine%2C%20FL!5e0!3m2!1sen!2sus!4v1704067200000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Service Area Map"
                className="grayscale"
              />
            </motion.div>

            {/* Areas List */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-12 h-px bg-[#C6A87C] mb-8" />
              <h2 className="font-heading text-3xl md:text-4xl mb-8">Primary Service Areas</h2>
              <div className="space-y-6">
                {serviceAreas.map((area) => (
                  <div key={area.name} className="flex items-start gap-4">
                    <MapPin className={`w-5 h-5 mt-1 flex-shrink-0 ${area.primary ? 'text-[#C6A87C]' : 'text-gray-400'}`} />
                    <div>
                      <h3 className="font-semibold mb-1">{area.name}</h3>
                      <p className="text-gray-600 text-sm">{area.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-24 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl mb-4">Neighborhoods We Serve</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From historic districts to new developments, we bring quality craftsmanship to communities across Northeast Florida.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4"
          >
            {neighborhoods.map((neighborhood) => (
              <div
                key={neighborhood}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <CheckCircle className="w-4 h-4 text-[#C6A87C] flex-shrink-0" />
                <span>{neighborhood}</span>
              </div>
            ))}
          </motion.div>

          <p className="text-center text-gray-500 text-sm mt-8">
            Don't see your area? Contact us—we may still be able to help!
          </p>
        </div>
      </section>

      {/* Local Expertise */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-12 h-px bg-[#C6A87C] mb-8" />
              <h2 className="font-heading text-3xl md:text-4xl mb-6">Local Expertise</h2>
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  Based in St. Augustine, we understand the unique character and needs of Northeast Florida homes. From historic properties requiring sensitive restoration to modern builds seeking contemporary updates, we have the experience to deliver.
                </p>
                <p>
                  Our knowledge of local building requirements, climate considerations, and regional design preferences ensures your project is handled with expertise from start to finish.
                </p>
                <p>
                  We're not just contractors—we're your neighbors, invested in the quality and beauty of our shared community.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1578365743977-d11ff2b7e9cd?auto=format&fit=crop&w=1200&q=80"
                  alt="St. Augustine historic district"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-px bg-[#C6A87C] mx-auto mb-8" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-gray-400 text-lg mb-10">
              Contact us today to discuss your remodeling needs and schedule a consultation.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="tel:9048636673"
                data-testid="areas-call-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#C6A87C] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
              >
                <Phone className="w-4 h-4" />
                (904) 863-6673
              </a>
              <Link
                to="/contact"
                data-testid="areas-contact-btn"
                className="inline-flex items-center justify-center gap-3 bg-white text-[#0A0A0A] px-8 py-4 text-xs font-semibold uppercase tracking-widest hover:bg-gray-100 transition-colors"
              >
                Request Estimate
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
